// ============================================================================
// ADVOCATE SERVICE
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { advocateEndpoints, ADVOCATE_BASE_PATH } from '../endpoints/advocate.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Advocate,
  AdvocateListItem,
  CreateAdvocateRequest,
  UpdateAdvocateRequest,
  AdvocateSearchRequest,
} from '../types/advocate.types';

class AdvocateService {
  async getAll(params?: AdvocateSearchRequest): Promise<PaginatedResponse<AdvocateListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(ADVOCATE_BASE_PATH, params)
      : advocateEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Advocate>> {
    const response = await get<ApiResponse<Advocate>>(advocateEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Advocate>> {
    const response = await get<ApiResponse<Advocate>>(advocateEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateAdvocateRequest): Promise<ApiResponse<Advocate>> {
    const response = await post<ApiResponse<Advocate>>(advocateEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateAdvocateRequest): Promise<ApiResponse<Advocate>> {
    const response = await put<ApiResponse<Advocate>>(advocateEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(advocateEndpoints.delete(id));
    return response.data;
  }

  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    const endpoint = excludeId
      ? `${advocateEndpoints.validateCode(code)}?excludeId=${excludeId}`
      : advocateEndpoints.validateCode(code);
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

export const advocateService = new AdvocateService();
export default advocateService;
